import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play, X } from 'lucide-react';
import { useVoiceovers } from './VoiceoverContext';

const GAP_MS = 1200;
const SILENT_MS = 5000;

const useIdle = (ms) => {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    let t;
    const wake = () => { setIdle(false); clearTimeout(t); t = setTimeout(() => setIdle(true), ms); };
    wake();
    window.addEventListener('mousemove', wake);
    return () => { clearTimeout(t); window.removeEventListener('mousemove', wake); };
  }, [ms]);
  return idle;
};

const useNarration = ({ slide, auto, onEnd }) => {
  const { items, audioUrl, claim } = useVoiceovers();
  const audio = useRef(null);
  useEffect(() => {
    let timer;
    let cancelled = false;
    const done = () => { timer = setTimeout(onEnd, GAP_MS); };
    claim(null);
    if (slide.ref && items[slide.ref]) {
      audioUrl(slide.ref).then((url) => {
        if (cancelled) return;
        const a = new Audio(url);
        audio.current = a;
        claim(() => a.pause());
        a.onended = () => auto && done();
        a.play().catch(() => {});
      }).catch(() => auto && done());
    } else if (auto) {
      timer = setTimeout(onEnd, SILENT_MS);
    }
    return () => { cancelled = true; clearTimeout(timer); audio.current?.pause(); audio.current = null; };
  }, [slide, auto, items, audioUrl, onEnd, claim]);
};

export const PresenterMode = ({ slides, render, onClose }) => {
  const [i, setI] = useState(0);
  const [auto, setAuto] = useState(true);
  const idle = useIdle(2500);
  const next = useCallback(() => setI((x) => Math.min(x + 1, slides.length - 1)), [slides.length]);
  const prev = useCallback(() => setI((x) => Math.max(x - 1, 0)), []);
  const slide = slides[i];
  const onEnd = useCallback(() => (i < slides.length - 1 ? next() : setAuto(false)), [i, slides.length, next]);
  useNarration({ slide, auto, onEnd });

  useEffect(() => {
    document.documentElement.requestFullscreen?.().catch(() => {});
    const onFs = () => { if (!document.fullscreenElement) onClose(); };
    const onKey = (e) => {
      if (['ArrowRight', 'PageDown', ' '].includes(e.key)) { e.preventDefault(); next(); }
      else if (['ArrowLeft', 'PageUp'].includes(e.key)) { e.preventDefault(); prev(); }
      else if (e.key === 'Home') setI(0);
      else if (e.key === 'End') setI(slides.length - 1);
      else if (e.key.toLowerCase() === 'a') setAuto((v) => !v);
      else if (e.key === 'Escape') onClose();
    };
    document.addEventListener('fullscreenchange', onFs);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      window.removeEventListener('keydown', onKey);
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, [next, prev, onClose, slides.length]);

  return (
    <div className={`sc-present ${idle ? 'sc-idle' : ''}`} data-testid="presenter-mode">
      <div className="sc-present-stage" key={i} data-testid={`presenter-slide-${i}`}>{render(slide)}</div>
      <div className="sc-present-hud" data-testid="presenter-hud">
        <button type="button" onClick={prev} disabled={i === 0} data-testid="presenter-prev" aria-label="Previous"><ChevronLeft size={18} /></button>
        <span data-testid="presenter-counter">{i + 1} / {slides.length}</span>
        <button type="button" onClick={next} disabled={i === slides.length - 1} data-testid="presenter-next" aria-label="Next"><ChevronRight size={18} /></button>
        <button type="button" onClick={() => setAuto((v) => !v)} data-testid="presenter-autoplay-toggle">
          {auto ? <Pause size={16} /> : <Play size={16} />} {auto ? 'Auto-play on' : 'Auto-play off'}
        </button>
        <span className="sc-present-hint">← → to move · A auto-play · Esc to exit</span>
        <button type="button" onClick={onClose} data-testid="presenter-exit" aria-label="Exit"><X size={18} /></button>
      </div>
    </div>
  );
};
